// The canvas's right panel: everything about the selected tile.
//
// The panel writes through `lib/chart-edit.ts` rather than reaching into the
// spec, and revalidates through `lib/built-chart.ts` on every keystroke. Both
// are pure and tested; this file is the arrangement of controls over them, and
// deliberately holds no rule of its own — a rule that lives only in a component
// is a rule no test can reach and the server does not share.
//
// **Every select here offers only legal choices.** The aggregate list for a
// column comes from its BigQuery type, the sort list from this chart's own
// columns and metric labels, the annotation sources from the sources that
// actually carry a time column and a label. That is manners rather than
// security — `validate_built_chart` on the server is the gate, and it runs
// again in the worker — but it is the difference between an editor that cannot
// compose an illegal chart and one that lets you and then refuses.
import type {
  BiSource,
  BiSourceSummary,
  ChartSpec,
  NativeFilterSpec,
  SimpleAggregate,
} from "@bi/shared/types";

import { Button, Input } from "@bi/shared";

import { aggregatesForType, validateBuiltChart } from "../../lib/built-chart";
import {
  TIME_GRAINS,
  TIME_GRAIN_LABELS,
  defaultAnnotations,
  getAnnotations,
  getBreakdown,
  getColorScheme,
  getDimension,
  getMeasures,
  getNumberFormat,
  getRowLimit,
  getSort,
  getTimeGrain,
  maxMeasures,
  newMeasure,
  setAnnotations,
  setBreakdown,
  setChartDescription,
  setChartTitle,
  setColorScheme,
  setDimension,
  setMeasureAggregate,
  setMeasures,
  setNumberFormat,
  setRowLimit,
  setSort,
  setTimeGrain,
  setTrendLine,
  sortableNames,
} from "../../lib/chart-edit";
import { CHART_TILE_KINDS, TILE_KIND_LABELS, type TileKind, tileKindOf } from "../../lib/studio-model";

/** The named colour schemes a built chart may pick.
 *
 *  A short closed list, not a free text box: the scheme name is read by the
 *  renderer's own palette resolution, and a name nothing recognises silently
 *  falls back — which reads as "the colour picker does nothing". */
const COLOR_SCHEMES = ["supersetColors", "miraSequential", "miraDiverging"] as const;

/** Formats offered as one click each, with the free box still underneath. The
 *  syntax is d3's and nobody remembers it; three worked examples are worth more
 *  than a link to the grammar. */
const NUMBER_FORMAT_PRESETS: { label: string; value: string | null }[] = [
  { label: "Default", value: null },
  { label: "1,234", value: ",.0f" },
  { label: "1.2k", value: ".3s" },
  { label: "12.3%", value: ".1%" },
  { label: "$1,234", value: "$,.0f" },
];

export interface PropertyPanelProps {
  chart: ChartSpec | null;
  /** The selected tile's own source, resolved. `null` while it loads. */
  source: BiSource | null;
  /** Every source, for the source picker and the annotation events picker. */
  sources: readonly BiSourceSummary[];
  /** The full detail of every source this dashboard already reads, so the
   *  annotation editor can offer real column names rather than a text box. */
  sourcesById: Readonly<Record<string, BiSource>>;
  onChartChange: (chart: ChartSpec) => void;
  onSourceChange: (sourceId: string) => void;
  onKindChange: (kind: TileKind) => void;
  onDelete: () => void;
  /** A markdown tile is selected instead of a chart. */
  markdown?: { value: string; onChange: (next: string) => void } | null;
  /** A control is selected instead of a tile. */
  control?: ControlEditorProps | null;
  /** What the last preview said this chart would scan. */
  bytesEstimate?: number | null;
}

export function PropertyPanel(props: PropertyPanelProps) {
  if (props.control) {
    return (
      <aside className="bi-studio-panel" aria-label="Control properties">
        <ControlEditor {...props.control} sources={props.sources} sourcesById={props.sourcesById} />
      </aside>
    );
  }
  if (props.markdown) {
    return (
      <aside className="bi-studio-panel" aria-label="Text properties">
        <h2 className="bi-studio-panel__title">Text</h2>
        <label className="bi-studio-field">
          <span className="bi-studio-field__label">Markdown</span>
          <textarea
            className="bi-studio-textarea"
            rows={12}
            value={props.markdown.value}
            onChange={(e) => props.markdown?.onChange(e.target.value)}
          />
        </label>
        <Button variant="outline" tone="danger" size="sm" onClick={props.onDelete}>
          Delete tile
        </Button>
      </aside>
    );
  }
  if (!props.chart) {
    return (
      <aside className="bi-studio-panel" aria-label="Tile properties">
        <p className="bi-studio-panel__empty">
          Select a tile to edit it, or add one from the toolbar above the canvas.
        </p>
      </aside>
    );
  }
  return <ChartProperties {...props} chart={props.chart} />;
}

function ChartProperties({
  chart,
  source,
  sources,
  sourcesById,
  onChartChange,
  onSourceChange,
  onKindChange,
  onDelete,
  bytesEstimate,
}: PropertyPanelProps & { chart: ChartSpec }) {
  const issues = validateBuiltChart(chart, source);
  const kind = tileKindOf(chart);
  const measures = getMeasures(chart);
  const columns = source?.columns ?? [];
  const dimensions = columns.filter((c) => c.role !== "measure");
  const sort = getSort(chart);
  const annotations = getAnnotations(chart);
  const colorScheme = getColorScheme(chart);
  const numberFormat = getNumberFormat(chart);
  const isTimeSeries =
    chart.viz_type === "echarts_timeseries_bar" || chart.viz_type === "echarts_timeseries_line";
  const isScorecard = chart.viz_type === "big_number" || chart.viz_type === "big_number_total";
  // Only a source that can actually carry a mark set is offered — one with no
  // time column has nowhere to put a mark, and picking it would compose a spec
  // the server refuses on save.
  const annotationSources = sources.filter((s) => {
    const detail = sourcesById[s.id];
    return detail ? defaultAnnotations(detail) !== null : s.id === annotations?.source_id;
  });

  return (
    <aside className="bi-studio-panel" aria-label="Tile properties">
      <h2 className="bi-studio-panel__title">{TILE_KIND_LABELS[kind]}</h2>

      {issues.length > 0 ? (
        <ul className="bi-studio-panel__issues" aria-label="Problems with this tile">
          {issues.map((issue) => (
            <li key={`${issue.field}:${issue.message}`}>{issue.message}</li>
          ))}
        </ul>
      ) : null}

      <Field label="Title">
        <Input
          value={chart.slice_name}
          onChange={(e) => onChartChange(setChartTitle(chart, e.target.value))}
        />
      </Field>

      <Field label="Description" hint="Shown on hover. Often the only place a number's meaning is written down.">
        <textarea
          className="bi-studio-textarea"
          rows={2}
          value={chart.description ?? ""}
          onChange={(e) => onChartChange(setChartDescription(chart, e.target.value))}
        />
      </Field>

      <Field label="Data source">
        <select
          className="bi-studio-select"
          value={chart.source_id ?? ""}
          onChange={(e) => onSourceChange(e.target.value)}
        >
          {sources.map((s) => (
            <option key={s.id} value={s.id}>
              {s.title}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Chart type">
        <select
          className="bi-studio-select"
          value={kind}
          onChange={(e) => onKindChange(e.target.value as TileKind)}
        >
          {CHART_TILE_KINDS.map((k) => (
            <option key={k} value={k}>
              {TILE_KIND_LABELS[k]}
            </option>
          ))}
        </select>
      </Field>

      {chart.query.query_mode === "raw" ? null : (
        <>
          <Field label={isTimeSeries ? "Time axis" : "Dimension"}>
            <select
              className="bi-studio-select"
              value={getDimension(chart) ?? ""}
              onChange={(e) => onChartChange(setDimension(chart, e.target.value || null))}
            >
              <option value="">None</option>
              {(isTimeSeries ? columns.filter((c) => c.role === "time") : dimensions).map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Breakdown" hint="One series per value of this column.">
            <select
              className="bi-studio-select"
              value={getBreakdown(chart) ?? ""}
              onChange={(e) => onChartChange(setBreakdown(chart, e.target.value || null))}
            >
              <option value="">None</option>
              {dimensions.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>

          <fieldset className="bi-studio-fieldset">
            <legend className="bi-studio-field__label">Measures</legend>
            {measures.map((measure, index) => {
              const column = columns.find((c) => c.name === measure.column);
              const allowed = column ? aggregatesForType(column.bq_type) : (["count"] as SimpleAggregate[]);
              return (
                <div key={`${measure.column}-${index}`} className="bi-studio-measure">
                  <select
                    className="bi-studio-select"
                    aria-label={`Aggregate for ${measure.column ?? "rows"}`}
                    value={measure.aggregate}
                    onChange={(e) => {
                      const next = [...measures];
                      next[index] = setMeasureAggregate(measure, e.target.value as SimpleAggregate);
                      onChartChange(setMeasures(chart, next));
                    }}
                  >
                    {allowed.map((agg) => (
                      <option key={agg} value={agg}>
                        {agg}
                      </option>
                    ))}
                  </select>
                  <select
                    className="bi-studio-select"
                    aria-label={`Column for ${measure.label}`}
                    value={measure.column ?? ""}
                    onChange={(e) => {
                      const picked = columns.find((c) => c.name === e.target.value);
                      const next = [...measures];
                      next[index] = picked
                        ? newMeasure(picked)
                        : { ...measure, column: null, aggregate: "count", label: "Rows" };
                      onChartChange(setMeasures(chart, next));
                    }}
                  >
                    <option value="">Rows (count)</option>
                    {columns.map((c) => (
                      <option key={c.name} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <Input
                    aria-label={`Label for ${measure.column ?? "rows"}`}
                    value={measure.label}
                    onChange={(e) => {
                      const next = [...measures];
                      next[index] = { ...measure, label: e.target.value };
                      onChartChange(setMeasures(chart, next));
                    }}
                  />
                  <Button
                    variant="ghost"
                    tone="neutral"
                    size="sm"
                    aria-label={`Remove ${measure.label}`}
                    onClick={() =>
                      onChartChange(setMeasures(chart, measures.filter((_, i) => i !== index)))
                    }
                  >
                    Remove
                  </Button>
                </div>
              );
            })}
            {measures.length < maxMeasures(chart) ? (
              <Button
                variant="outline"
                tone="neutral"
                size="sm"
                disabled={columns.length === 0}
                onClick={() => {
                  const measureColumn =
                    columns.find((c) => c.role === "measure" && !measures.some((m) => m.column === c.name)) ??
                    columns[0];
                  if (measureColumn) {
                    onChartChange(setMeasures(chart, [...measures, newMeasure(measureColumn)]));
                  }
                }}
              >
                Add a measure
              </Button>
            ) : (
              <p className="bi-studio-field__hint">
                A {TILE_KIND_LABELS[kind].toLowerCase()} shows one measure.
              </p>
            )}
          </fieldset>

          <Field label="Sort by">
            <div className="bi-studio-row">
              <select
                className="bi-studio-select"
                value={sort?.column ?? ""}
                onChange={(e) => onChartChange(setSort(chart, e.target.value || null, sort?.ascending ?? false))}
              >
                <option value="">Default</option>
                {sortableNames(chart, source).map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
              <select
                className="bi-studio-select"
                aria-label="Sort direction"
                disabled={!sort}
                value={sort?.ascending ? "asc" : "desc"}
                onChange={(e) =>
                  onChartChange(setSort(chart, sort?.column ?? null, e.target.value === "asc"))
                }
              >
                <option value="desc">Highest first</option>
                <option value="asc">Lowest first</option>
              </select>
            </div>
          </Field>
        </>
      )}

      <Field label="Row limit" hint="Refused above this environment's ceiling rather than quietly truncated.">
        <Input
          type="number"
          min={1}
          value={getRowLimit(chart) ?? ""}
          onChange={(e) =>
            onChartChange(setRowLimit(chart, e.target.value ? Number(e.target.value) : null))
          }
        />
      </Field>

      {isTimeSeries || isScorecard ? (
        <Field
          label="Time grain"
          hint="The bucket width asked for. A window too wide for it is compiled coarser, and the card says so."
        >
          <select
            className="bi-studio-select"
            value={getTimeGrain(chart) ?? ""}
            onChange={(e) => onChartChange(setTimeGrain(chart, e.target.value || null))}
          >
            <option value="">None</option>
            {TIME_GRAINS.map((grain) => (
              <option key={grain} value={grain}>
                {TIME_GRAIN_LABELS[grain]}
              </option>
            ))}
          </select>
        </Field>
      ) : null}

      {isScorecard ? (
        <label className="bi-studio-check">
          <input
            type="checkbox"
            checked={chart.viz_type === "big_number"}
            onChange={(e) => onChartChange(setTrendLine(chart, e.target.checked, source))}
          />
          <span>Show a trend line under the number</span>
        </label>
      ) : null}

      {colorScheme !== null ? (
        <Field label="Colours">
          <select
            className="bi-studio-select"
            value={colorScheme}
            onChange={(e) => onChartChange(setColorScheme(chart, e.target.value))}
          >
            {COLOR_SCHEMES.map((scheme) => (
              <option key={scheme} value={scheme}>
                {scheme}
              </option>
            ))}
          </select>
        </Field>
      ) : null}

      {getNumberFormat(chart) !== null || COLOR_SCHEMES.length > 0 ? (
        <Field label="Number format">
          <div className="bi-studio-row bi-studio-row--wrap">
            {NUMBER_FORMAT_PRESETS.map((preset) => (
              <Button
                key={preset.label}
                variant={numberFormat === preset.value ? "solid" : "outline"}
                tone="neutral"
                size="sm"
                onClick={() => onChartChange(setNumberFormat(chart, preset.value))}
              >
                {preset.label}
              </Button>
            ))}
          </div>
          <Input
            aria-label="Number format"
            placeholder="d3 format, for example ,.1f"
            value={numberFormat ?? ""}
            onChange={(e) => onChartChange(setNumberFormat(chart, e.target.value))}
          />
        </Field>
      ) : null}

      {isTimeSeries ? (
        <fieldset className="bi-studio-fieldset">
          <legend className="bi-studio-field__label">Annotations</legend>
          <label className="bi-studio-check">
            <input
              type="checkbox"
              checked={annotations !== null}
              disabled={annotationSources.length === 0}
              onChange={(e) => {
                if (!e.target.checked) {
                  onChartChange(setAnnotations(chart, null));
                  return;
                }
                const first = annotationSources[0];
                const detail = first ? sourcesById[first.id] : undefined;
                const spec = detail ? defaultAnnotations(detail) : null;
                if (spec) onChartChange(setAnnotations(chart, spec));
              }}
            />
            <span>Mark events on this chart</span>
          </label>
          {annotationSources.length === 0 ? (
            <p className="bi-studio-field__hint">
              No source here carries the time and label columns a mark set needs.
            </p>
          ) : null}
          {annotations ? (
            <>
              <Field label="Events source">
                <select
                  className="bi-studio-select"
                  value={annotations.source_id}
                  onChange={(e) => {
                    const detail = sourcesById[e.target.value];
                    const spec = detail ? defaultAnnotations(detail) : null;
                    if (spec) onChartChange(setAnnotations(chart, spec));
                  }}
                >
                  {annotationSources.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.title}
                    </option>
                  ))}
                </select>
              </Field>
              <AnnotationColumns
                annotations={annotations}
                source={sourcesById[annotations.source_id]}
                onChange={(next) => onChartChange(setAnnotations(chart, next))}
              />
            </>
          ) : null}
        </fieldset>
      ) : null}

      {bytesEstimate != null ? (
        <p className="bi-studio-field__hint">
          This chart scans about {Math.max(1, Math.round(bytesEstimate / 1024 / 1024))} MB.
        </p>
      ) : null}

      <Button variant="outline" tone="danger" size="sm" onClick={onDelete}>
        Delete tile
      </Button>
    </aside>
  );
}

function AnnotationColumns({
  annotations,
  source,
  onChange,
}: {
  annotations: NonNullable<ReturnType<typeof getAnnotations>>;
  source: BiSource | undefined;
  onChange: (next: NonNullable<ReturnType<typeof getAnnotations>>) => void;
}) {
  const columns = source?.columns ?? [];
  const times = columns.filter((c) => c.role === "time");
  const labels = columns.filter((c) => c.role !== "time");
  if (columns.length === 0) return null;
  return (
    <>
      <Field label="Event time">
        <select
          className="bi-studio-select"
          value={annotations.at_column}
          onChange={(e) => onChange({ ...annotations, at_column: e.target.value })}
        >
          {times.map((c) => (
            <option key={c.name} value={c.name}>
              {c.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Event label">
        <select
          className="bi-studio-select"
          value={annotations.label_column}
          onChange={(e) => onChange({ ...annotations, label_column: e.target.value })}
        >
          {labels.map((c) => (
            <option key={c.name} value={c.name}>
              {c.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Event kind" hint="Groups the marks by colour. Lifecycle, deploy, whatever the source uses.">
        <select
          className="bi-studio-select"
          value={annotations.kind_column}
          onChange={(e) => onChange({ ...annotations, kind_column: e.target.value })}
        >
          {labels.map((c) => (
            <option key={c.name} value={c.name}>
              {c.name}
            </option>
          ))}
        </select>
      </Field>
    </>
  );
}

export interface ControlEditorProps {
  control: NativeFilterSpec;
  onChange: (next: NativeFilterSpec) => void;
  onDelete: () => void;
  /** The parameter bound to this control, if any: a URL value that seeds its
   *  selection at open time, which is what makes a dashboard a TEMPLATE. */
  parameterName: string | null;
  onParameterChange: (name: string | null) => void;
}

export function ControlEditor({
  control,
  onChange,
  onDelete,
  parameterName,
  onParameterChange,
  sources,
  sourcesById,
}: ControlEditorProps & {
  sources: readonly BiSourceSummary[];
  sourcesById: Readonly<Record<string, BiSource>>;
}) {
  const from = control.options_from;
  const optionSource = from ? sourcesById[from.source_id] : undefined;
  return (
    <>
      <h2 className="bi-studio-panel__title">Control</h2>
      <Field label="Name">
        <Input value={control.name} onChange={(e) => onChange({ ...control, name: e.target.value })} />
      </Field>
      <Field label="Kind">
        <select
          className="bi-studio-select"
          value={control.kind}
          onChange={(e) =>
            onChange({ ...control, kind: e.target.value as NativeFilterSpec["kind"] })
          }
        >
          <option value="select">Select a value</option>
          <option value="time_range">Date range</option>
          <option value="range">Number range</option>
        </select>
      </Field>

      {control.kind === "select" ? (
        <>
          <Field
            label="Column"
            hint="Applies to every chart whose source declares this column. That auto-scoping is why the list is not per chart."
          >
            <Input
              value={control.target_column ?? ""}
              onChange={(e) => onChange({ ...control, target_column: e.target.value || null })}
            />
          </Field>
          <Field label="Options from" hint="The values people pick between, read live from a source.">
            <select
              className="bi-studio-select"
              value={from?.source_id ?? ""}
              onChange={(e) =>
                onChange({
                  ...control,
                  options_from: e.target.value
                    ? { source_id: e.target.value, column: control.target_column ?? "" }
                    : null,
                })
              }
            >
              <option value="">Typed in by the reader</option>
              {sources.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title}
                </option>
              ))}
            </select>
          </Field>
          {from ? (
            <Field label="Options column">
              <select
                className="bi-studio-select"
                value={from.column}
                onChange={(e) =>
                  onChange({ ...control, options_from: { ...from, column: e.target.value } })
                }
              >
                <option value="">Pick a column</option>
                {(optionSource?.columns ?? []).map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
          <label className="bi-studio-check">
            <input
              type="checkbox"
              checked={control.multi_select}
              onChange={(e) => onChange({ ...control, multi_select: e.target.checked })}
            />
            <span>Allow more than one value</span>
          </label>
        </>
      ) : null}

      {control.kind === "range" ? (
        <>
          <p className="bi-studio-field__hint">
            A number range binds a pair of DATASET parameters, so the bound applies inside the
            source's own aggregation. Name the two the source declares.
          </p>
          <Field label="Low parameter">
            <Input
              value={control.target_parameter_low ?? ""}
              onChange={(e) =>
                onChange({ ...control, target_parameter_low: e.target.value || null })
              }
            />
          </Field>
          <Field label="High parameter">
            <Input
              value={control.target_parameter_high ?? ""}
              onChange={(e) =>
                onChange({ ...control, target_parameter_high: e.target.value || null })
              }
            />
          </Field>
        </>
      ) : null}

      <Field
        label="URL parameter"
        hint="Seeds this control from the address bar, which is what turns this dashboard into a template."
      >
        <Input
          value={parameterName ?? ""}
          placeholder="for example, experiment"
          onChange={(e) => onParameterChange(e.target.value.trim() || null)}
        />
      </Field>

      <Button variant="outline" tone="danger" size="sm" onClick={onDelete}>
        Delete control
      </Button>
    </>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="bi-studio-field">
      <span className="bi-studio-field__label">{label}</span>
      {children}
      {hint ? <span className="bi-studio-field__hint">{hint}</span> : null}
    </label>
  );
}
